import React, { useState } from "react";
import { Product } from "../../data/catalog";
import {
  ChevronDown,
  ChevronUp,
  Tag,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import ImageLightbox from "../ui/ImageLightbox";
import OptimizedImage from "../ui/OptimizedImage";
import { isVideoUrl } from "../../utils/mediaUtils";
import OrderWhatsAppModal from "./OrderWhatsAppModal";

interface Props {
  product: Product & { images?: string[] };
  onOpenDetail?: (product: Product) => void;
}

const badgeColors: Record<string, string> = {
  Favorita: "bg-rose-100 text-rose-700",
  Popular: "bg-pink-100 text-pink-700",
  Especial: "bg-amber-100 text-amber-700",
  Nuevo: "bg-emerald-100 text-emerald-700",
};

const ProductCard: React.FC<Props> = ({ product, onOpenDetail }) => {
  const [expanded, setExpanded] = useState(false);
  const [selectedSize, setSelectedSize] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [orderModalOpen, setOrderModalOpen] = useState(false);

  const handleOpenDetailModal = () => {
    if (onOpenDetail) {
      onOpenDetail(product);
    } else {
      window.location.hash = `#postre-${product.id}`;
    }
  };

  // Get all images for carousel
  const allImages = product.images
    ? product.images
    : product.image
      ? product.image.includes(",") ? product.image.split(",").map((s: string) => s.trim()) : [product.image]
      : [];
  const hasMultipleImages = allImages.length > 1;
  const currentImage = allImages[currentImageIndex];
  const currentIsVideo = isVideoUrl(currentImage);
  const lowestPrice = Math.min(...product.prices.map((p) => p.price));

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + allImages.length) % allImages.length,
    );
  };

  return (
    <>
      <div className="cake-card h-full w-full bg-white rounded-2xl overflow-hidden border border-rose-100/60 shadow-sm hover:border-rose-200 flex flex-col justify-between">
        {/* Image container */}
        <div className="relative h-48 bg-rose-50 overflow-hidden cursor-pointer group flex-shrink-0" onClick={handleOpenDetailModal}>
          {currentImage ? (
            <>
              {!currentIsVideo && !imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-rose-50">
                  <span className="text-4xl opacity-30">{product.emoji}</span>
                </div>
              )}
              {currentIsVideo ? (
                <video
                  src={currentImage}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxImage(currentImage);
                  }}
                />
              ) : (
                <OptimizedImage
                  path={currentImage}
                  alt={product.name}
                  width={600}
                  useSrcSet={true}
                  sizes="(max-width: 640px) 400px, (max-width: 1024px) 600px, 800px"
                  className={`w-full h-full object-cover transition-opacity duration-300 group-hover:scale-105 ${
                    imageLoaded ? "opacity-100" : "opacity-0"
                  }`}
                  onLoad={() => setImageLoaded(true)}
                />
              )}

              {/* Hover overlay hint */}
              <div className="absolute inset-0 bg-rose-950/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                <span className="bg-white/90 backdrop-blur-sm text-rose-900 text-xs font-semibold px-3 py-1 rounded-full shadow-md">
                  Ver detalle 🔍
                </span>
              </div>

              {/* Carousel controls for multiple images */}
              {hasMultipleImages && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      prevImage();
                    }}
                    aria-label="Imagen anterior"
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-1.5 rounded-full shadow-md transition-all z-10">
                    <ChevronLeft className="w-3 h-3 text-rose-700" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      nextImage();
                    }}
                    aria-label="Imagen siguiente"
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-1.5 rounded-full shadow-md transition-all z-10">
                    <ChevronRight className="w-3 h-3 text-rose-700" />
                  </button>
                  {/* Image indicators */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10" role="group" aria-label="Seleccionar imagen">
                    {allImages.map((_, i) => (
                      <button
                        key={i}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex(i);
                        }}
                        aria-label={`Imagen ${i + 1} de ${allImages.length}`}
                        aria-current={i === currentImageIndex ? "true" : undefined}
                        className={`w-1.5 h-1.5 rounded-full transition-all ${
                          i === currentImageIndex
                            ? "bg-rose-600 w-4"
                            : "bg-white/60"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-6xl opacity-50">{product.emoji}</span>
            </div>
          )}
          {/* Top color strip */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-rose-400 via-pink-400 to-rose-500" />
        </div>

        <div className="p-5 flex-1 flex flex-col justify-between">
          {/* Header & Description */}
          <div>
            {/* Header */}
            <div className="flex items-start justify-between mb-3 cursor-pointer gap-2" onClick={handleOpenDetailModal}>
              <div className="flex items-start gap-2.5 min-w-0 flex-1">
                <span className="text-3xl flex-shrink-0">{product.emoji}</span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-base font-semibold text-rose-900 leading-tight hover:text-rose-700 transition-colors line-clamp-2 min-h-[2.5rem] flex items-center">
                    {product.name}
                  </h3>
                  <div className="min-h-[1.25rem] mt-1 flex items-center">
                    {product.badge && (
                      <span
                        className={`inline-block text-[10px] font-body font-medium px-2 py-0.5 rounded-full ${
                          badgeColors[product.badge] || "bg-rose-100 text-rose-700"
                        }`}>
                        {product.badge}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right flex-shrink-0 pl-1">
                <span className="font-body text-xs text-rose-600 block">desde</span>
                <p className="font-display text-xl font-bold text-rose-700 leading-tight">
                  <span className="text-xs font-normal">Ref</span> {lowestPrice}
                </p>
              </div>
            </div>

            {/* Description */}
            <div className="mb-4 min-h-[2.75rem] flex flex-col justify-center">
              <p className="font-body text-sm text-gray-600 leading-relaxed line-clamp-2" title={product.description}>
                {product.description}
              </p>
            </div>
          </div>

          {/* Bottom actions anchored to bottom */}
          <div className="mt-auto flex flex-col">
            {/* Prices toggle */}
            {product.prices.length > 1 ? (
              <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between bg-rose-50 hover:bg-rose-100 rounded-xl px-4 py-2.5 transition-colors group h-10">
                <div className="flex items-center gap-2">
                  <Tag className="w-3.5 h-3.5 text-rose-500" />
                  <span className="font-body text-xs font-medium text-rose-700">
                    Ver precios ({product.prices.length} tamaños)
                  </span>
                </div>
                {expanded ? (
                  <ChevronUp className="w-4 h-4 text-rose-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-rose-400" />
                )}
              </button>
            ) : (
              <div className="bg-rose-50 rounded-xl px-4 py-2.5 flex items-center justify-between h-10">
                <span className="font-body text-xs text-rose-600">
                  {product.prices[selectedSize]?.people || "Tamaño único"}
                </span>
                <span className="font-display text-sm font-bold text-rose-700">
                  <span className="text-xs font-normal">Ref</span>{" "}
                  {product.prices[selectedSize]?.price || lowestPrice}
                </span>
              </div>
            )}

            {/* Expanded price list */}
            {expanded && product.prices.length > 1 && (
              <div className="mt-2 space-y-1.5">
                {product.prices.map((price, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedSize(i)}
                    className={`w-full flex items-center justify-between rounded-xl px-4 py-2 transition-all ${
                      selectedSize === i
                        ? "bg-rose-100 border-2 border-rose-300"
                        : "bg-white border border-rose-100 hover:border-rose-200"
                    }`}>
                    <span
                      className={`font-body text-xs ${
                        selectedSize === i
                          ? "text-rose-700 font-medium"
                          : "text-gray-600"
                      }`}>
                      {price.people}
                    </span>
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-display text-sm font-bold ${
                          selectedSize === i ? "text-rose-700" : "text-rose-600"
                        }`}>
                        <span className="text-xs font-normal">Ref</span>{" "}
                        {price.price}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Order button */}
            <button
              type="button"
              onClick={() => {
                if (typeof (window as any).clarity === "function") {
                  (window as any).clarity("set", "producto_whatsapp", product.name);
                  (window as any).clarity("event", "click_whatsapp");
                }
                setOrderModalOpen(true);
              }}
              className="mt-3 w-full flex items-center justify-center gap-1.5 bg-rose-700 hover:bg-rose-800 text-white font-body text-xs font-semibold py-2.5 rounded-xl transition-all duration-200 hover:shadow-md">
              <span>Pedir por WhatsApp</span>
              <span className="opacity-60 font-normal">•</span>
              <span className="bg-white/10 px-2 py-0.5 rounded-md">Ref {product.prices[selectedSize]?.price || lowestPrice}</span>
            </button>

            <button
              type="button"
              onClick={handleOpenDetailModal}
              className="mt-2 w-full text-center text-[11px] font-medium text-rose-600/80 hover:text-rose-800 py-1 transition-colors flex items-center justify-center gap-1">
              <span>Ver fotos y detalles completos</span>
              <span>→</span>
            </button>
          </div>
        </div>
      </div>
      <ImageLightbox
        image={lightboxImage}
        onClose={() => setLightboxImage(null)}
        allImages={allImages}
        currentImageIndex={currentImageIndex}
      />
      <OrderWhatsAppModal
        product={product}
        priceOption={product.prices[selectedSize]}
        isOpen={orderModalOpen}
        onClose={() => setOrderModalOpen(false)}
      />
    </>
  );
};

export default ProductCard;
